import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ref, onValue, remove, push } from "firebase/database";
import { database } from '../../../utils/firebase';
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
    DialogTitle,
    FormControl,
    FormLabel,
    Modal,
    ModalDialog,
    Stack,
    Button,
    MenuButton,
    Menu,
    MenuItem, Dropdown, ListItemDecorator, ListDivider, Select, Option
} from "@mui/joy";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import "./League_Dashboard.css";
import PrintIcon from "@mui/icons-material/Print";
import QuickReportGenerator from "../QuickReportGenerator/QuickReportGenerator";
import TablePagination from '@mui/material/TablePagination';
import DeleteForever from "@mui/icons-material/DeleteForever";
import {Add, MoreVert} from "@mui/icons-material";


const App = () => {
    const navigate = useNavigate();

    const {leagueId} = useParams();
    const leagueRef = ref(database, `leagues/${leagueId}`);
    const matchesRef = ref(database, `leagues/${leagueId}/matches`);
    const [leagueData, setLeagueData] = useState({});
    const [matchIds, setMatchIds] = useState([]);
    const [matchData, setMatchData] = useState({});
    const [newMatchOpen, setNewMatchOpen] = useState(false);
    const [newMatchDate, setNewMatchDate] = useState(dayjs(Date.now()));

    const [reportModalOpen, setReportModalOpen] = useState(false);
    const [reportType, setReportType] = useState('');
    const [reportId, setReportId] = useState('');

    const [availableYears, setAvailableYears] = useState([]);
    const [availableMonths, setAvailableMonths] = useState([]);
    const [selectedYear, setSelectedYear] = useState(dayjs().year());
    const [selectedMonth, setSelectedMonth] = useState(dayjs().month() + 1);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    useEffect(() => {
        const unsubscribe = onValue(leagueRef, (snapshot) => {
            const data = snapshot.val();
            setLeagueData(data);
        });

        return () => unsubscribe();
    }, []);

    useEffect(() => {
        if (!matchData || Object.keys(matchData).length === 0) return;

        const allDates = Object.values(matchData).map(m => dayjs.unix(m.dateTime));
        const yearSet = new Set();
        const monthMap = {};

        allDates.forEach(date => {
            const year = date.year();
            const month = date.month() + 1; // 1-based
            yearSet.add(year);
            if (!monthMap[year]) {
                monthMap[year] = new Set();
            }
            monthMap[year].add(month);
        });

        const sortedYears = Array.from(yearSet).sort((a, b) => a - b);
        const currentMonth = dayjs().month() + 1;

        setAvailableYears(sortedYears);
        if (!sortedYears.includes(selectedYear)) {
            setSelectedYear(sortedYears[sortedYears.length - 1]);
        }

        const months = monthMap[selectedYear] ? Array.from(monthMap[selectedYear]).sort((a, b) => a - b) : [];
        setAvailableMonths(months);

        // Adjust selectedMonth based on availability
        if (!months.includes(selectedMonth)) {
            if (months.includes(currentMonth)) {
                setSelectedMonth(currentMonth);
            } else if (months.length > 0) {
                setSelectedMonth(months[months.length - 1]);
            }
        }
    }, [matchData, selectedYear]);

    useEffect(() => {
        const unsubscribe = onValue(matchesRef, (snapshot) => {
            const data = snapshot.val();
            setMatchData(data);
            const ids = data ? Object.keys(data) : [];
            setMatchIds(ids);
        });

        return () => unsubscribe();
    }, []);

    const handleNewMatch = async () => {
        setNewMatchOpen(true);
    }

    const handleMatchOpen = (matchId) => {
        navigate(`/app/league/${leagueId}/match/${matchId}`);
    }

    const handleUnixTime = (unixTime) => {
        const date = new Date(unixTime * 1000);
        const options = {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            hour12: true,
            timeZone: leagueData.settings.timeZone
        };
        return date.toLocaleString(leagueData.settings.language, options);
    };

    const handleMatchDelete = (matchId) => {
        if (window.confirm("Are you sure you want to delete this match?")) {
            remove(ref(database, `leagues/${leagueId}/matches/${matchId}`));
        }
    };

    const handleCreate = () => {
        const newMatch = {
            dateTime: newMatchDate.unix(),
        };
        push(matchesRef, newMatch);
        setNewMatchOpen(false);
    };

    return (
        <div>
            <div className="container">
                <h1>{leagueData?.name || 'Loading...'}</h1>
                <p className="ldb-location-name">{leagueData?.parentGroup || ''}</p>
                <div className='split-view'>
                    <div className="content-pane pane">
                        <div className="ldb-month-selection">
                            <div className="ldb-year-selector">
                                <Select
                                    value={selectedYear}
                                    onChange={(_event, newValue) => {
                                        setSelectedYear(newValue);
                                        setPage(0);
                                    }}
                                >
                                    {availableYears.map((year) => (
                                        <Option key={year} value={year}>
                                            {year}
                                        </Option>
                                    ))}
                                </Select>
                            </div>
                            <div className="ldb-month-selector">
                                <Select
                                    value={selectedMonth}
                                    onChange={(_event, newValue) => {
                                        setSelectedMonth(newValue);
                                        setPage(0);
                                    }}
                                >
                                    {availableMonths.map((month) => (
                                        <Option key={month} value={month}>
                                            {dayjs().month(month - 1).format("MMMM")}
                                        </Option>
                                    ))}
                                </Select>
                            </div>
                            <div className={"dash-league-item-buttons"}>
                                <Button className="button-gradient ldb-new-button" onClick={handleNewMatch} startDecorator={<Add/>}>New Match</Button>
                                <Dropdown>
                                    <MenuButton className={"button-gradient"}><PrintIcon sx={{color: "white"}}/></MenuButton>
                                    <Menu>
                                        <MenuItem onClick={
                                            () => {
                                                setReportModalOpen(true);
                                                setReportType('matchSchedule');
                                            }}>Match Schedule</MenuItem>
                                    </Menu>
                                </Dropdown>
                            </div>
                        </div>
                        <h2>Matches</h2>
                        <div className="ldb-matches-list">
                            {matchIds.length === 0 ? (<div>No matches found</div>):(
                                <div>
                                    {matchIds
                                        .filter((matchId) => {
                                            const match = matchData[matchId];
                                            const date = dayjs.unix(match.dateTime);
                                            return date.year() === selectedYear && date.month() + 1 === selectedMonth;
                                        })
                                        .sort((a, b) => matchData[a].dateTime - matchData[b].dateTime)
                                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        .map((matchId) => (
                                        <div className="ldb-match">
                                            {handleUnixTime(matchData[matchId].dateTime)}
                                            <div className="dash-league-item-buttons">
                                                <Button className={'button-gradient'} onClick={() => handleMatchOpen(matchId)}>Open</Button>
                                                <Dropdown>
                                                    <MenuButton variant={"plain"} sx={{width: "15px"}}>
                                                        <MoreVert/>
                                                    </MenuButton>
                                                    <Menu placement="bottom-end">
                                                        <MenuItem onClick={
                                                            () => {
                                                                setReportModalOpen(true);
                                                                setReportType('matchReport');
                                                                setReportId(matchId);
                                                            }}>
                                                            <ListItemDecorator sx={{color: 'inherit'}}><PrintIcon/></ListItemDecorator>{" "}
                                                            Match Report
                                                        </MenuItem>
                                                        <MenuItem onClick={
                                                            () => {
                                                                setReportModalOpen(true);
                                                                setReportType('winnersReport');
                                                                setReportId(matchId);
                                                            }}>
                                                            <ListItemDecorator sx={{color: 'inherit'}}><PrintIcon/></ListItemDecorator>{" "}
                                                            Winners Report
                                                        </MenuItem>
                                                        <MenuItem onClick={
                                                            () => {
                                                                setReportModalOpen(true);
                                                                setReportType('blankScoreSheet');
                                                                setReportId(matchId);
                                                            }}>
                                                            <ListItemDecorator sx={{color: 'inherit'}}><PrintIcon/></ListItemDecorator>{" "}
                                                            Blank Score Sheet
                                                        </MenuItem>
                                                        <ListDivider />
                                                        <MenuItem variant="soft" color="danger" onClick={() => handleMatchDelete(matchId)}>
                                                            <ListItemDecorator sx={{ color: 'inherit'}}><DeleteForever/></ListItemDecorator>{" "}
                                                            Delete
                                                        </MenuItem>
                                                    </Menu>
                                                </Dropdown>
                                            </div>
                                        </div>
                                    ))}
                                    <TablePagination
                                        component="div"
                                        count={
                                            matchIds.filter((matchId) => {
                                                const match = matchData[matchId];
                                                const date = dayjs.unix(match.dateTime);
                                                return date.year() === selectedYear && date.month() + 1 === selectedMonth;
                                            }).length
                                        }
                                        page={page}
                                        onPageChange={(event, newPage) => setPage(newPage)}
                                        rowsPerPage={rowsPerPage}
                                        onRowsPerPageChange={(event) => {
                                            setRowsPerPage(parseInt(event.target.value, 10));
                                            setPage(0);
                                        }}
                                        rowsPerPageOptions={[5, 10, 25, 50]}
                                    />
                                </div>
                            )}
                        </div>

                    </div>
                    <div className="extra-pane pane">
                        <Button className={'button-gradient wide-button'} onClick={() => navigate(`/app/league/${leagueId}/players`)}>Players</Button>
                        <Button className={'button-gradient wide-button'} onClick={() => navigate(`/app/league/${leagueId}/handicaps`)}>Handicaps & Flights</Button>
                        <Button className={'button-gradient wide-button'} onClick={() => navigate(`/app/league/${leagueId}/payouts`)}>Payout Settings</Button>
                        <Button className={'button-gradient wide-button'} onClick={() => navigate(`/app/league/${leagueId}/settings`)}>Settings</Button>
                        <Button className={'button-gradient wide-button'} onClick={() => navigate(`/app/league/${leagueId}/reports`)}>Reports</Button>
                    </div>
                </div>
            </div>

            <Modal open={newMatchOpen} onClose={() => setNewMatchOpen(false)}>
                <ModalDialog>
                    <DialogTitle>Create new match</DialogTitle>
                    <form
                        onSubmit={(event) => {
                            event.preventDefault();
                            setNewMatchOpen(false);
                            handleCreate();
                        }}
                    >
                        <Stack spacing={2}>
                            <FormControl>
                                <FormLabel>Date</FormLabel>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DateTimePicker
                                        value={newMatchDate}
                                        onChange={(newDate) => setNewMatchDate(newDate)}
                                    />
                                </LocalizationProvider>
                            </FormControl>
                            <Button type="submit">Create</Button>
                        </Stack>
                    </form>
                </ModalDialog>
            </Modal>
            <QuickReportGenerator
                type={reportType}
                id={reportId}
                modalOpen={reportModalOpen}
                closeModal={() => setReportModalOpen(false)}
                leagueId={leagueId}
            />
        </div>
    );
}

export default App;